import { TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import {
  Checkbox,
  fontWeights,
  Paragraph,
  paragraphSizes,
  SelectDropdown,
  SelectDropdownOption,
  TextInput,
} from '@va/ui/design-system';
import { getBusinessSectorOptions, getBusinessSizeOptions, getBusinessTypeOptions } from '@va/util/helpers';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';

export type ShortBusinessInfoFormValues = {
  companyName: string;
  businessType: string;
  businessSize: string;
  businessSector: string;
  doNotShowAgain: boolean;
};

type ShortBusinessInfoFormProps = {
  id: string;
  form: ReturnType<typeof useFormik<ShortBusinessInfoFormValues>>;
  size?: 'medium' | 'large';
};

const formFieldNames = {
  companyName: 'companyName',
  businessType: 'businessType',
  businessSize: 'businessSize',
  businessSector: 'businessSector',
  doNotShowAgain: 'doNotShowAgain',
};

export const ShortBusinessInfoForm = ({ id, form, size = 'large' }: ShortBusinessInfoFormProps) => {
  const translate = useTranslate();
  const { handleSubmit, handleChange, values, errors, setFieldValue } = form;

  const businessSectorOptions: SelectDropdownOption<number>[] = useMemo(
    () => getBusinessSectorOptions(translate),
    [translate],
  );

  const getSelectedBusinessSector = useCallback(
    (businessSectorId: number | string) => {
      return businessSectorOptions.find(
        (option) =>
          Number(option.value) ===
          (typeof businessSectorId === 'string' ? Number.parseInt(businessSectorId) : businessSectorId),
      );
    },
    [businessSectorOptions],
  );

  const businessTypeOptions = useMemo(() => getBusinessTypeOptions(translate), [translate]);
  const getSelectedBusinessType = useCallback(
    (businessTypeId: number | string) => {
      return businessTypeOptions.find((option) => Number(option.value) === Number(businessTypeId));
    },
    [businessTypeOptions],
  );

  const businessSizeOptions = useMemo(() => getBusinessSizeOptions(translate), [translate]);
  const getSelectedBusinessSize = useCallback(
    (businessSizeId: number | string) => {
      return businessSizeOptions.find((option) => Number(option.value) === Number(businessSizeId));
    },
    [businessSizeOptions],
  );

  return (
    <form id={id} onSubmit={handleSubmit} className='flex flex-col gap-1.5 p-3'>
      <Paragraph
        className='px-3 pb-1.5'
        size={paragraphSizes.tiny}
        weight={fontWeights.medium}
        colorClassName='text-gray-devilSolid'
      >
        {translate('modal.shortBusinessInfo.description')}
      </Paragraph>
      <SelectDropdown
        label={translate('dropdown.businessData.businessType.placeholder')}
        selectedOption={getSelectedBusinessType(values.businessType)}
        dropdownOptions={businessTypeOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.businessType, option.value);
        }}
        error={errors.businessType}
        dataTestId={TEST_IDS.helpers.createFormElementId(id, formFieldNames.businessType)}
        size={size}
      />
      <SelectDropdown
        label={translate('dropdown.businessData.businessSize.placeholder')}
        selectedOption={getSelectedBusinessSize(values.businessSize)}
        dropdownOptions={businessSizeOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.businessSize, option.value);
        }}
        error={errors.businessSize}
        dataTestId={TEST_IDS.helpers.createFormElementId(id, formFieldNames.businessSize)}
        size={size}
      />
      <SelectDropdown
        label={translate('dropdown.businessData.businessSector.placeholder')}
        selectedOption={getSelectedBusinessSector(values.businessSector)}
        dropdownOptions={businessSectorOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.businessSector, option.value);
        }}
        error={errors.businessSector}
        dataTestId={TEST_IDS.helpers.createFormElementId(id, formFieldNames.businessSector)}
        size={size}
      />
      <TextInput
        error={errors.companyName as string}
        name={formFieldNames.companyName}
        label={translate('form.placeholders.companyName')}
        onChange={handleChange}
        value={values.companyName}
        clearField={() => {
          setFieldValue(formFieldNames.companyName, '');
        }}
        dataTestId={TEST_IDS.helpers.createFormElementId(id, formFieldNames.companyName)}
        heightClass={size === 'medium' ? 'h-12' : undefined}
      />
      <Checkbox
        name={formFieldNames.doNotShowAgain}
        label={translate('modal.shortBusinessInfo.doNotShowAgain')}
        value={values.doNotShowAgain}
        onChange={(value) => {
          setFieldValue(formFieldNames.doNotShowAgain, value);
        }}
        labelClassName='font-500 text-gray-charcoal text-15px! leading-6'
        wrapperClassName='mt-1 p-3'
        dataTestId={TEST_IDS.helpers.createFormElementId(id, formFieldNames.doNotShowAgain)}
      />
    </form>
  );
};
